import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { api } from '../services/api';
import {
  User,
  Target,
  Award,
  Sparkles,
  CheckCircle,
  X,
  ArrowRight,
  ArrowLeft,
  Plus,
  Compass,
  Briefcase,
  MapPin,
  Check
} from 'lucide-react';

const AVATAR_PRESETS = [
  { id: 'dev-1', label: 'Tech Lead', bg: 'from-blue-600 to-indigo-600', emoji: '👨‍💻' },
  { id: 'dev-2', label: 'Frontend Dev', bg: 'from-emerald-500 to-teal-600', emoji: '👩‍💻' },
  { id: 'dev-3', label: 'AI Engineer', bg: 'from-purple-600 to-pink-600', emoji: '🤖' },
  { id: 'dev-4', label: 'Cloud Architect', bg: 'from-amber-500 to-orange-600', emoji: '☁️' },
  { id: 'dev-5', label: 'UI/UX Designer', bg: 'from-rose-500 to-red-600', emoji: '🎨' },
  { id: 'dev-6', label: 'Product Manager', bg: 'from-cyan-500 to-blue-600', emoji: '🚀' },
];

const POPULAR_SKILLS = [
  { name: 'React', category: 'Frontend', level: 'Advanced' },
  { name: 'Python', category: 'Backend', level: 'Intermediate' },
  { name: 'TypeScript', category: 'Frontend', level: 'Intermediate' },
  { name: 'Node.js', category: 'Backend', level: 'Intermediate' },
  { name: 'Docker', category: 'Infrastructure', level: 'Intermediate' },
  { name: 'PostgreSQL', category: 'Database', level: 'Intermediate' },
  { name: 'UI/UX Design', category: 'Design', level: 'Advanced' },
  { name: 'Machine Learning', category: 'AI/ML', level: 'Beginner' },
  { name: 'FastAPI', category: 'Backend', level: 'Intermediate' },
  { name: 'AWS', category: 'Infrastructure', level: 'Intermediate' },
];

const POPULAR_CERTS = [
  { name: 'AWS Certified Solutions Architect', issuer: 'Amazon Web Services', year: '2024' },
  { name: 'Meta Front-End Developer', issuer: 'Meta', year: '2024' },
  { name: 'Google Cloud Professional', issuer: 'Google Cloud', year: '2024' },
  { name: 'Professional Scrum Master (PSM I)', issuer: 'Scrum.org', year: '2023' },
  { name: 'Certified Kubernetes Administrator (CKA)', issuer: 'Linux Foundation', year: '2024' }
];

const OnboardingModal = () => {
  const { isDarkMode } = useTheme();
  const { currentUser, updateUserProfile, isOnboardingOpen, closeOnboarding } = useAuth();

  const [step, setStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);

  // Form State
  const [formData, setFormData] = useState(() => ({
    name: currentUser?.name || '',
    title: currentUser?.title || 'Full-Stack Developer',
    bio: currentUser?.bio || '',
    location: currentUser?.location || 'Bucharest, Romania',
    availability: currentUser?.availability || 'Available',
    avatar: currentUser?.avatar || 'dev-1',
    avatarUrl: currentUser?.avatarUrl || '',
    skills: currentUser?.skills || [],
    certifications: currentUser?.certifications || []
  }));

  // New Skill inline input
  const [newSkill, setNewSkill] = useState({
    name: '',
    category: 'Frontend',
    level: 'Intermediate',
    experienceYears: 2
  });

  // New Cert inline input
  const [newCert, setNewCert] = useState({
    name: '',
    issuer: '',
    year: new Date().getFullYear().toString(),
    credentialUrl: ''
  });

  if (!isOnboardingOpen) return null;

  const handleNext = () => setStep(prev => Math.min(prev + 1, 4));
  const handlePrev = () => setStep(prev => Math.max(prev - 1, 1));

  const handleAddSkill = (skillToAdd) => {
    const skill = skillToAdd || newSkill;
    if (!skill.name.trim()) return;
    if (formData.skills.some(s => s.name.toLowerCase() === skill.name.trim().toLowerCase())) return;

    setFormData(prev => ({
      ...prev,
      skills: [...prev.skills, {
        name: skill.name.trim(),
        category: skill.category || 'General',
        level: skill.level || 'Intermediate',
        experienceYears: Number(skill.experienceYears) || 1
      }]
    }));

    if (!skillToAdd) {
      setNewSkill({ name: '', category: 'Frontend', level: 'Intermediate', experienceYears: 2 });
    }
  };

  const handleRemoveSkill = (skillName) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s.name !== skillName)
    }));
  };

  const handleAddCert = (certToAdd) => {
    const cert = certToAdd || newCert;
    if (!cert.name.trim()) return;
    if (formData.certifications.some(c => c.name.toLowerCase() === cert.name.trim().toLowerCase())) return;

    setFormData(prev => ({
      ...prev,
      certifications: [...prev.certifications, {
        name: cert.name.trim(),
        issuer: cert.issuer || 'Industry Certificate',
        year: cert.year || new Date().getFullYear().toString(),
        credentialUrl: cert.credentialUrl || ''
      }]
    }));

    if (!certToAdd) {
      setNewCert({ name: '', issuer: '', year: new Date().getFullYear().toString(), credentialUrl: '' });
    }
  };

  const handleRemoveCert = (certName) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter(c => c.name !== certName)
    }));
  };

  const handleComplete = async () => {
    setIsSaving(true);
    const maxExp = formData.skills.reduce((acc, curr) => Math.max(acc, Number(curr.experienceYears) || 0), 1);

    const updatedProfile = {
      name: formData.name.trim() || (currentUser?.email ? currentUser.email.split('@')[0] : 'SkillMesh Member'),
      title: formData.title.trim() || 'SkillMesh Member',
      bio: formData.bio.trim(),
      location: formData.location.trim(),
      availability: formData.availability,
      avatar: formData.avatar,
      avatarUrl: formData.avatarUrl,
      skills: formData.skills,
      certifications: formData.certifications,
      experienceYears: maxExp,
      onboardingCompleted: true
    };

    updateUserProfile(updatedProfile);

    // Sync to Neo4j directory and localStorage
    try {
      const personEntry = {
        id: currentUser?.id || `p_${Date.now()}`,
        name: updatedProfile.name,
        role: updatedProfile.title,
        team: updatedProfile.skills.length > 0 ? updatedProfile.skills[0].category : 'Engineering',
        email: currentUser?.email || 'user@skillmesh.io',
        location: updatedProfile.location,
        availability: updatedProfile.availability,
        skills: updatedProfile.skills.map(s => s.name),
        detailedSkills: updatedProfile.skills,
        experience: `${maxExp} years`,
        bio: updatedProfile.bio,
        avatar: updatedProfile.avatarUrl || updatedProfile.avatar,
        certifications: updatedProfile.certifications
      };

      await api.createPerson(personEntry);
    } catch (e) {
      console.warn('Fallback: saved onboarding locally:', e);
    }

    setIsSaving(false);
    closeOnboarding();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fadeIn">
      <div className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-3xl shadow-2xl border transition-all overflow-hidden ${
        isDarkMode ? 'bg-dark-900 border-dark-700 text-white' : 'bg-white border-light-200 text-light-950'
      }`}>
        {/* Header */}
        <div className={`p-6 border-b flex items-center justify-between ${
          isDarkMode ? 'border-dark-800 bg-dark-950/50' : 'border-light-200 bg-light-50'
        }`}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center text-white shadow-md">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Welcome to SkillMesh!</h2>
              <p className="text-xs text-gray-400">Step {step} of 4 • Set up your profile for project & team matching</p>
            </div>
          </div>
          <button
            onClick={closeOnboarding}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-dark-800"
            title="Complete later"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-dark-800 h-1.5">
          <div
            className="h-full bg-gradient-primary transition-all duration-300"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* STEP 1: Basic Info & Avatar */}
          {step === 1 && (
            <div className="space-y-5 animate-fadeIn">
              <div>
                <h3 className="text-lg font-bold mb-1 flex items-center space-x-2">
                  <User className="h-5 w-5 text-primary-400" />
                  <span>Choose Your Avatar & Identity</span>
                </h3>
                <p className="text-xs text-gray-400">
                  Select a representative avatar, role, and short bio so teammates can recognize you.
                </p>
              </div>

              {/* Avatar Preset Grid */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-gray-400">
                  Select Avatar Preset
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {AVATAR_PRESETS.map((p) => {
                    const isSelected = formData.avatar === p.id && !formData.avatarUrl;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, avatar: p.id, avatarUrl: '' }))}
                        className={`flex flex-col items-center p-3 rounded-2xl border transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-500/10 ring-2 ring-primary-500/50 shadow-lg scale-105'
                            : isDarkMode ? 'border-dark-700 bg-dark-800 hover:border-dark-600' : 'border-light-200 bg-light-100 hover:border-light-300'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${p.bg} flex items-center justify-center text-2xl shadow-md mb-1.5`}>
                          {p.emoji}
                        </div>
                        <span className="text-[11px] font-medium truncate w-full text-center">{p.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Image URL (optional) */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1 text-gray-400">
                  Or Custom Photo / Image URL (Optional)
                </label>
                <input
                  type="url"
                  placeholder="https://example.com/my-photo.jpg"
                  value={formData.avatarUrl}
                  onChange={(e) => setFormData(prev => ({ ...prev, avatarUrl: e.target.value }))}
                  className={`input-field w-full text-xs ${isDarkMode ? 'dark' : 'light'}`}
                />
              </div>

              {/* Name & Role */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1 text-gray-400">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Andrei Ionescu"
                    className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1 text-gray-400">
                    Professional Title / Role
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. Senior Frontend Engineer"
                    className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1 text-gray-400">
                  Location / City
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g. Bucharest, Romania / Remote"
                    className={`input-field w-full pl-9 text-sm ${isDarkMode ? 'dark' : 'light'}`}
                  />
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1 text-gray-400">
                  Short Description / Bio
                </label>
                <textarea
                  rows={3}
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Share a sentence or two about your expertise, background, or what you enjoy building..."
                  className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                />
              </div>
            </div>
          )}

          {/* STEP 2: Skills */}
          {step === 2 && (
            <div className="space-y-5 animate-fadeIn">
              <div>
                <h3 className="text-lg font-bold mb-1 flex items-center space-x-2">
                  <Target className="h-5 w-5 text-primary-400" />
                  <span>Your Skills & Expertise</span>
                </h3>
                <p className="text-xs text-gray-400">
                  SkillMesh uses these skills to calculate your compatibility score for open projects.
                </p>
              </div>

              {/* Quick Select Popular Skills */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-gray-400">
                  Quick Add Popular Skills (1-Click)
                </label>
                <div className="flex flex-wrap gap-2">
                  {POPULAR_SKILLS.map((ps) => {
                    const isAdded = formData.skills.some(s => s.name.toLowerCase() === ps.name.toLowerCase());
                    return (
                      <button
                        key={ps.name}
                        type="button"
                        onClick={() => isAdded ? handleRemoveSkill(ps.name) : handleAddSkill(ps)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border flex items-center space-x-1.5 transition-all ${
                          isAdded
                            ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                            : isDarkMode
                              ? 'bg-dark-800 text-dark-300 border-dark-700 hover:border-primary-500 hover:text-white'
                              : 'bg-light-100 text-light-700 border-light-200 hover:border-primary-500 hover:text-primary-600'
                        }`}
                      >
                        {isAdded ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                        <span>{ps.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Skill Input */}
              <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-dark-800/80 border-dark-700' : 'bg-light-100 border-light-200'}`}>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2">
                  Add Custom Skill
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
                  <div className="sm:col-span-2">
                    <input
                      type="text"
                      placeholder="e.g. Next.js, Kubernetes, NLP"
                      value={newSkill.name}
                      onChange={(e) => setNewSkill(prev => ({ ...prev, name: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSkill();
                        }
                      }}
                      className={`input-field w-full text-xs ${isDarkMode ? 'dark' : 'light'}`}
                    />
                  </div>
                  <div>
                    <select
                      value={newSkill.level}
                      onChange={(e) => setNewSkill(prev => ({ ...prev, level: e.target.value }))}
                      className={`input-field w-full text-xs ${isDarkMode ? 'dark' : 'light'}`}
                    >
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                      <option value="Expert">Expert</option>
                    </select>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => handleAddSkill()}
                      className="btn-primary w-full py-2 text-xs flex items-center justify-center space-x-1"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span>Add</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Added Skills List */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-gray-400">
                  Your Listed Skills ({formData.skills.length})
                </label>
                <div className="flex flex-wrap gap-2">
                  {formData.skills.map((skill) => (
                    <span
                      key={skill.name}
                      className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-lg text-xs font-medium border ${
                        isDarkMode ? 'bg-dark-700 border-dark-600 text-white' : 'bg-light-200 border-light-300 text-light-900'
                      }`}
                    >
                      <span className="font-semibold">{skill.name}</span>
                      <span className="opacity-70 text-[10px]">({skill.level})</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveSkill(skill.name)}
                        className="text-red-400 hover:text-red-300 p-0.5 ml-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {formData.skills.length === 0 && (
                    <p className="text-xs text-amber-400 italic">No skills added yet. Select some popular skills above or type a custom one!</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Certifications */}
          {step === 3 && (
            <div className="space-y-5 animate-fadeIn">
              <div>
                <h3 className="text-lg font-bold mb-1 flex items-center space-x-2">
                  <Award className="h-5 w-5 text-amber-400" />
                  <span>Certifications & Badges</span>
                </h3>
                <p className="text-xs text-gray-400">
                  Add verified certifications to stand out to project creators and team leaders.
                </p>
              </div>

              {/* Popular Certifications 1-Click */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-gray-400">
                  Popular Certifications (1-Click Add)
                </label>
                <div className="space-y-1.5">
                  {POPULAR_CERTS.map((pc) => {
                    const isAdded = formData.certifications.some(c => c.name.toLowerCase() === pc.name.toLowerCase());
                    return (
                      <div
                        key={pc.name}
                        className={`flex items-center justify-between p-2.5 rounded-xl border text-xs transition-all ${
                          isAdded
                            ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                            : isDarkMode ? 'bg-dark-800 border-dark-700 text-dark-200' : 'bg-light-100 border-light-200 text-light-800'
                        }`}
                      >
                        <div className="flex items-center space-x-2 truncate">
                          <Award className="h-4 w-4 text-amber-400 flex-shrink-0" />
                          <div className="truncate">
                            <span className="font-semibold truncate">{pc.name}</span>
                            <span className="text-[10px] opacity-70 ml-2">({pc.issuer})</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => isAdded ? handleRemoveCert(pc.name) : handleAddCert(pc)}
                          className={`px-2.5 py-1 rounded text-[11px] font-semibold flex items-center space-x-1 ${
                            isAdded
                              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                              : 'bg-primary-500 text-white hover:bg-primary-600'
                          }`}
                        >
                          {isAdded ? (
                            <>
                              <X className="h-3 w-3" />
                              <span>Remove</span>
                            </>
                          ) : (
                            <>
                              <Plus className="h-3 w-3" />
                              <span>Add</span>
                            </>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom Cert Input */}
              <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-dark-800/80 border-dark-700' : 'bg-light-100 border-light-200'}`}>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2">
                  Add Custom Certification
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                  <div className="sm:col-span-2">
                    <input
                      type="text"
                      placeholder="Certification name (e.g. Cisco CCNA)"
                      value={newCert.name}
                      onChange={(e) => setNewCert(prev => ({ ...prev, name: e.target.value }))}
                      className={`input-field w-full text-xs ${isDarkMode ? 'dark' : 'light'}`}
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Issuer (e.g. Cisco)"
                      value={newCert.issuer}
                      onChange={(e) => setNewCert(prev => ({ ...prev, issuer: e.target.value }))}
                      className={`input-field w-full text-xs ${isDarkMode ? 'dark' : 'light'}`}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleAddCert()}
                  className="btn-secondary py-1.5 px-3 text-xs flex items-center space-x-1"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Add Custom Certificate</span>
                </button>
              </div>

              {/* Added Certs List */}
              {formData.certifications.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-gray-400">
                    Your Certifications ({formData.certifications.length})
                  </label>
                  <div className="space-y-1.5">
                    {formData.certifications.map(c => (
                      <div
                        key={c.name}
                        className={`flex items-center justify-between p-2.5 rounded-lg border text-xs ${
                          isDarkMode ? 'bg-dark-700/60 border-dark-600' : 'bg-light-100 border-light-200'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Award className="h-4 w-4 text-amber-400" />
                          <span className="font-semibold">{c.name}</span>
                          <span className="opacity-70 text-[11px]">• {c.issuer} ({c.year})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveCert(c.name)}
                          className="text-red-400 hover:text-red-300 p-0.5"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 4: Preferences & Interface Familiarization */}
          {step === 4 && (
            <div className="space-y-5 animate-fadeIn">
              <div>
                <h3 className="text-lg font-bold mb-1 flex items-center space-x-2">
                  <Compass className="h-5 w-5 text-primary-400" />
                  <span>Availability & How SkillMesh Works</span>
                </h3>
                <p className="text-xs text-gray-400">
                  Quick orientation so you can navigate and make the most out of the platform.
                </p>
              </div>

              {/* Availability setting */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-gray-400">
                  Current Availability for Projects
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {['Available', 'Partially Available', 'Not Available'].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, availability: opt }))}
                      className={`p-3 rounded-xl border text-xs font-semibold text-center transition-all ${
                        formData.availability === opt
                          ? 'border-primary-500 bg-primary-500/10 text-primary-300 ring-2 ring-primary-500/50 shadow-md'
                          : isDarkMode ? 'border-dark-700 bg-dark-800 text-dark-300' : 'border-light-200 bg-light-100 text-light-700'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Familiarization Guide */}
              <div className={`p-4 rounded-2xl border space-y-3 ${
                isDarkMode ? 'bg-dark-800/60 border-dark-700' : 'bg-light-100 border-light-200'
              }`}>
                <h4 className="text-xs font-bold uppercase tracking-wider text-primary-400">
                  🧭 Quick Guide to the SkillMesh Interface
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div className={`p-3 rounded-xl border ${isDarkMode ? 'bg-dark-900/60 border-dark-700' : 'bg-white border-light-200'}`}>
                    <div className="font-bold mb-1 flex items-center space-x-1.5 text-blue-400">
                      <User className="h-4 w-4" />
                      <span>1. My Profile</span>
                    </div>
                    <p className="text-gray-400 leading-relaxed text-[11px]">
                      Access your profile anytime via the sidebar or top bar to edit skills, certifications, and bio.
                    </p>
                  </div>

                  <div className={`p-3 rounded-xl border ${isDarkMode ? 'bg-dark-900/60 border-dark-700' : 'bg-white border-light-200'}`}>
                    <div className="font-bold mb-1 flex items-center space-x-1.5 text-emerald-400">
                      <Briefcase className="h-4 w-4" />
                      <span>2. Projects</span>
                    </div>
                    <p className="text-gray-400 leading-relaxed text-[11px]">
                      Browse collaborative projects, see your % skill fit, apply with 1-click, or publish a new vision.
                    </p>
                  </div>

                  <div className={`p-3 rounded-xl border ${isDarkMode ? 'bg-dark-900/60 border-dark-700' : 'bg-white border-light-200'}`}>
                    <div className="font-bold mb-1 flex items-center space-x-1.5 text-purple-400">
                      <Sparkles className="h-4 w-4" />
                      <span>3. Matching</span>
                    </div>
                    <p className="text-gray-400 leading-relaxed text-[11px]">
                      Our graph algorithm connects project requirements with the right talent across the network.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className={`p-6 border-t flex items-center justify-between ${
          isDarkMode ? 'border-dark-800 bg-dark-950/50' : 'border-light-200 bg-light-50'
        }`}>
          <div>
            {step > 1 ? (
              <button
                type="button"
                onClick={handlePrev}
                className={`btn-secondary py-2 px-4 text-xs flex items-center space-x-1.5 ${isDarkMode ? 'dark' : 'light'}`}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                <span>Back</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={closeOnboarding}
                className="text-xs text-gray-400 hover:underline"
              >
                Skip for now
              </button>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {step < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="btn-primary py-2 px-5 text-xs flex items-center space-x-1.5"
              >
                <span>Continue</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleComplete}
                disabled={isSaving}
                className="btn-primary py-2.5 px-6 text-xs flex items-center space-x-2 shadow-lg"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Saving Profile...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span>Complete Profile & Start</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
